/**
 * StreakService
 * 
 * Manages practice streak data and calculations.
 * Tracks consecutive practice days and provides streak information.
 */

import authService from './AuthService';
import { getTimezoneHeaders, getAccountTimezone, rememberProtectionContext, getClientTimezone } from './StreakProtection';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function getStreakHeaders() {
  const token = authService.getAuthToken?.();
  const userId = authService.getCurrentUserId();
  return { ...getTimezoneHeaders(), ...(token ? { Authorization: `Bearer ${token}` } : userId ? { 'x-user-id': userId } : {}) };
}

class StreakService {
  /**
   * Get streak data from backend
   * @param {string} timezone - User's timezone (optional, defaults to browser timezone)
   * @returns {Promise<Object>} Streak data with streak count, hasPracticedToday, and lastPracticeDate
   * @throws {Error} Network or API errors
   */
  async getStreakData(timezone = null) {
    const tz = getAccountTimezone() || timezone || getClientTimezone() || 'UTC';
    const requestHeaders = { 'Content-Type': 'application/json', ...getStreakHeaders() };
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/practice/streak?timezone=${encodeURIComponent(tz)}`,
        {
          method: 'GET',
          headers: requestHeaders,
        }
      );

      if (response.status === 401) {
        authService.handleUnauthorized?.(requestHeaders.Authorization?.replace(/^Bearer\s+/i, ''));
      }

      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle HTTP errors
      if (!response.ok) {
        const error = new Error(
          data?.error?.message || data?.message || `HTTP ${response.status}: ${response.statusText}`
        );
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return rememberProtectionContext(data);
    } catch (error) {
      // Handle network errors
      if (error.name === 'TypeError' && (
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network request failed') ||
        error.message.includes('NetworkError')
      )) {
        const networkError = new Error('Network error: Unable to connect to the server. Please check your connection.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Calculate current streak from practice sessions (client-side)
   * @param {Array} sessions - Array of practice session objects
   * @param {string} timezone - User's timezone (optional, defaults to browser timezone)
   * @returns {number} Current streak count
   */
  calculateStreak(sessions, timezone = null) {
    if (!sessions || sessions.length === 0) {
      return 0;
    }

    const tz = getAccountTimezone() || timezone || getClientTimezone() || 'UTC';
    
    // Group sessions by date
    const dateMap = new Map();
    sessions.forEach(session => {
      const date = this._getDateInTimezone(session.startTime, tz);
      if (!dateMap.has(date)) {
        dateMap.set(date, true);
      }
    });

    // Get unique dates and sort descending
    const practiceDates = Array.from(dateMap.keys()).sort((a, b) => b.localeCompare(a));
    
    if (practiceDates.length === 0) {
      return 0;
    }

    // Get today's date in the specified timezone
    const today = this._getTodayInTimezone(tz);
    
    let streak = 0;
    let currentDate = today;

    // Count consecutive days backwards from today
    for (const practiceDate of practiceDates) {
      if (practiceDate === currentDate) {
        streak++;
        // Move to previous day
        currentDate = this._getPreviousDay(currentDate);
      } else if (practiceDate < currentDate) {
        // Gap found, streak ends
        break;
      }
    }

    return streak;
  }

  /**
   * Check if user has practiced today
   * @param {Array} sessions - Array of practice session objects
   * @param {string} timezone - User's timezone (optional, defaults to browser timezone)
   * @returns {boolean} True if practiced today
   */
  hasPracticedToday(sessions, timezone = null) {
    if (!sessions || sessions.length === 0) {
      return false;
    }

    const tz = getAccountTimezone() || timezone || getClientTimezone() || 'UTC';
    const today = this._getTodayInTimezone(tz);

    return sessions.some(session => {
      const sessionDate = this._getDateInTimezone(session.startTime, tz);
      return sessionDate === today;
    });
  }

  /**
   * Get date string (YYYY-MM-DD) from ISO timestamp in specified timezone
   * @private
   * @param {string} isoTimestamp - ISO 8601 timestamp
   * @param {string} timezone - Timezone string
   * @returns {string} Date in YYYY-MM-DD format
   */
  _getDateInTimezone(isoTimestamp, timezone) {
    const date = new Date(isoTimestamp);
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(date); // Returns YYYY-MM-DD
  }

  /**
   * Get today's date in specified timezone
   * @private
   * @param {string} timezone - Timezone string
   * @returns {string} Today's date in YYYY-MM-DD format
   */
  _getTodayInTimezone(timezone) {
    const now = new Date();
    return this._getDateInTimezone(now.toISOString(), timezone);
  }

  /**
   * Get previous day's date string
   * @private
   * @param {string} dateString - Date in YYYY-MM-DD format
   * @returns {string} Previous day in YYYY-MM-DD format
   */
  _getPreviousDay(dateString) {
    const date = new Date(dateString + 'T00:00:00Z');
    date.setUTCDate(date.getUTCDate() - 1);
    return date.toISOString().split('T')[0];
  }
}

export default StreakService;
